import { RentResponse } from '../../../../api/client/data-contracts';
import { QueriedData } from '../../assets/useAssets';
import { useRents } from '../useRents';

export interface StatisticEntry {
  id: string;
  value: number;
  description: string;
}

export interface UseRentStatisticsResult {
  numberOfActiveCustomers: number;
  numberOfRents: number;
  billing: number;
  arpaIndex: number;
  top5Customers: StatisticEntry[];
  top5Equipments: StatisticEntry[];
}

function calculateNumberOfActiveCustomers(rents: RentResponse[]) {
  const clientsSet: Record<string, string> = {};

  rents
    .filter((rent) => rent.state !== 'ORDERING')
    .forEach(
      (rent) => (clientsSet[rent.customer.id] = rent.customer.description),
    );

  return Object.entries(clientsSet).length;
}

function calculateBilled(rents: RentResponse[]) {
  const billed = rents
    .filter((rent) => rent.state !== 'ORDERING')
    .reduce((prev, curr) => prev + curr.paymentTotal, 0);

  return billed;
}

function calculateTopCustomers(rents: RentResponse[]) {
  const customerSet: Record<string, StatisticEntry> = {};

  rents.forEach((rent) => {
    const customerId = rent.customer.id;
    if (!customerSet[customerId]) {
      customerSet[customerId] = {
        id: customerId,
        description: rent.customer.description,
        value: 0,
      };
    }

    customerSet[customerId].value += rent.paymentTotal;
  });

  const customerPayment = Object.entries(customerSet).map(
    ([_, customerEntry]) => customerEntry,
  );

  // Sort desc by payment value
  const sortedCustomerPayment = customerPayment.sort(
    (customerA, customerB) => customerB.value - customerA.value,
  );

  const top5Customers = sortedCustomerPayment.slice(0, 5);
  const others = sortedCustomerPayment.slice(5);
  const otherCustomersTotal = others.reduce(
    (prev, curr) => prev + curr.value,
    0,
  );

  const othersEntry: StatisticEntry = {
    id: '__others__',
    description: 'Outros',
    value: otherCustomersTotal,
  };

  if (otherCustomersTotal > 0) {
    top5Customers.push(othersEntry);
  }
  return top5Customers;
}

function calculateTopEquipments(rents: RentResponse[]) {
  const equipmentsSet: Record<string, StatisticEntry> = {};

  rents.forEach((rent) => {
    rent.rentLines.forEach((line) => {
      const equipmentId = line.asset.serialNumber.toString();
      if (!equipmentsSet[equipmentId]) {
        equipmentsSet[equipmentId] = {
          id: equipmentId,
          description: equipmentId,
          value: 0,
        };
      }

      equipmentsSet[equipmentId].value += 1;
    });
  });

  const equipmentsPayment = Object.entries(equipmentsSet).map(
    ([_, customerEntry]) => customerEntry,
  );

  // Sort desc by payment value
  const sortedEquipmentPayment = equipmentsPayment.sort(
    (equipmentA, equipmentB) => equipmentB.value - equipmentA.value,
  );

  const others = sortedEquipmentPayment.slice(5);
  const otherEquipmentsTotal = others.reduce(
    (prev, curr) => prev + curr.value,
    0,
  );

  const othersEntry: StatisticEntry = {
    id: '__others__',
    description: 'Outros',
    value: otherEquipmentsTotal,
  };
  const top5Equipments = sortedEquipmentPayment.slice(0, 5);

  if (otherEquipmentsTotal > 0) {
    top5Equipments.push(othersEntry);
  }
  return top5Equipments;
}

export function useRentStatistics(startDateInterval: {
  begin: string;
  end: string;
}): UseRentStatisticsResult | null {
  const { rentsQuery } = useRents({ startDateInterval });
  if (!rentsQuery.data) {
    return null;
  }

  const rents = rentsQuery.data.filter((rent) => rent.state !== 'ORDERING');
  const numberOfActiveCustomers = calculateNumberOfActiveCustomers(rents);
  const numberOfRents = rents.length;
  const billing = calculateBilled(rents);

  let arpaIndex = 0;
  if (numberOfActiveCustomers > 0) {
    arpaIndex = billing / numberOfActiveCustomers;
  }
  const top5Customers = calculateTopCustomers(rents);
  const top5Equipments = calculateTopEquipments(rents);

  return {
    numberOfActiveCustomers,
    numberOfRents,
    billing,
    arpaIndex,
    top5Customers,
    top5Equipments,
  };
}

export interface UseYearRentsResult {
  yearRents: QueriedData<RentResponse[]>;
}

export function useYearRents(year: number): UseYearRentsResult {
  const startDate = new Date(year, 0, 1);
  const endDate = new Date(year, 11, 31);

  const useRentsResult = useRents({
    startDateInterval: {
      begin: startDate.toISOString(),
      end: endDate.toISOString(),
    },
  });

  return { yearRents: useRentsResult.rentsQuery };
}

export interface UseRentsPerMonthStatsResult {
  rentsPerMonth: RentResponse[][] | null;
}

export function useRentsPerMonthStats(
  year: number,
): UseRentsPerMonthStatsResult {
  const { yearRents } = useYearRents(year);

  if (!yearRents.data) {
    return { rentsPerMonth: null };
  }

  const monthsSet: Record<
    number,
    { monthIndex: number; rents: RentResponse[] }
  > = {};

  for (let i = 0; i < 12; ++i) {
    monthsSet[i] = { monthIndex: i, rents: [] };
  }

  yearRents.data
    .filter((rent) => rent.state !== 'ORDERING')
    .forEach((rent) => {
      const rentStartDate = rent.startDate;
      const rentStartDateMonth = new Date(rentStartDate).getMonth();

      monthsSet[rentStartDateMonth].rents.push(rent);
    });
  const rentsPerMonth = Object.values(monthsSet)
    .sort((monthA, monthB) => monthA.monthIndex - monthB.monthIndex)
    .map((entry) => entry.rents);

  return { rentsPerMonth };
}
